import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import LineChartComponent from "../components/LineChartComponent";
import BarChartComponent from "../components/BarChartComponent";
import PieChartComponent from "../components/PieChartComponent";
import RemainingOrderBalancesChart from "../components/RemainingOrderBalancesChart";
// import DoughnutChartComponent from "../components/DoughnutChartComponent";
import {
  SkeletonBarChart,
  SkeletonLineChart,
  SkeletonPieChart,
  // SkeletonDoughnutChart,
} from "../components/skeleton/SkeletonComponents";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
} from "chart.js";
import {
  fetchCotDashboard,
  fetchPrfDashboard,
} from "../actions/dashboardActions";
import axiosInstance from "../utils/axiosInstance";
// import { Link } from "react-router-dom";
import {
  FaGlobe,
  FaClock,
  FaPhone,
  FaExchangeAlt,
  FaChevronDown,
} from "react-icons/fa";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
);

const Home = () => {
  const [selectedYears, setSelectedYears] = useState({
    order: "all",
    declaration: "all",
    checks: "all",
    representation: "all",
    cottageBarYear: "all",
  });
  const [summaryData, setSummaryData] = useState({
    passed_checks_count: 0,
    unpassed_checks_count: 0,
    passed_checks_value: 0,
    unpassed_checks_value: 0,
    past_representations_count: 0,
    unpast_representations_count: 0,
  });
  const [dataType, setDataType] = useState("price");
  const [cryptoData, setCryptoData] = useState([]);
  const [cryptoLoading, setCryptoLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [countriesData, setCountriesData] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [currentCountriesPage, setCurrentCountriesPage] = useState(1);
  const [freeMarketData, setFreeMarketData] = useState([]);
  const [freeMarketLoading, setFreeMarketLoading] = useState(true);
  const [currentFreeMarketPage, setCurrentFreeMarketPage] = useState(1);
  const [converterData, setConverterData] = useState([]);
  const [converterLoading, setConverterLoading] = useState(true);
  const [amount, setAmount] = useState(1);
  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [conversionRate, setConversionRate] = useState(null);
  const [rateTimestamp, setRateTimestamp] = useState("");
  const itemsPerPage = 8;

  const dispatch = useDispatch();

  const handleYearChange = (cardType, year) => {
    setSelectedYears((prevSelectedYears) => ({
      ...prevSelectedYears,
      [cardType]: year,
    }));
    if (cardType === "order") {
      dispatch(fetchPrfDashboard(year === "all" ? "all" : year));
    } else if (cardType === "declaration") {
      dispatch(fetchCotDashboard(year === "all" ? "all" : year));
    }
  };

  const fetchSummaryData = async () => {
    try {
      const response = await axiosInstance.get("/rep-dashboard/");
      const data = response.data;
      setSummaryData(data);
    } catch (error) {
      console.error("Error fetching summary data:", error);
    }
  };

  const fetchCryptoData = async () => {
    try {
      setCryptoLoading(true);
      const response = await axiosInstance.get(
        "/customs/currency-exchange-rates/",
      );

      const validData = response.data
        .filter((item) => item.name && item.sell_price && item.slug)
        .map((item) => {
          // فقط slug و قیمت‌ها رو نگه دار
          return {
            slug: item.slug.toUpperCase(), // USD, EUR, ...
            label: item.name
              .replace(/ك/g, "ک")
              .replace(/ي/g, "ی")
              .replace(/\s+/g, " ")
              .trim(),
            value: parseFloat(item.sell_price.replace(/,/g, "")) || 0,
            buyPrice: parseFloat(item.buy_price.replace(/,/g, "")) || 0,
          };
        });

      setCryptoData(validData);
      setCryptoLoading(false);
    } catch (error) {
      console.error("Error fetching crypto data:", error);
      setCryptoLoading(false);
    }
  };
  const fetchFreeMarketData = async () => {
    try {
      setFreeMarketLoading(true);
      const response = await axiosInstance.get("/currencies/");
      const validData = response.data.Result.map((currency) => ({
        label: currency.name.trim(),
        value: parseFloat(currency.price.replace(/,/g, "")) || 0,
      }));
      setFreeMarketData(validData);
      setConverterData(validData); // Use free market data for converter
      setConverterLoading(false);
      setFreeMarketLoading(false);
    } catch (error) {
      console.error("Error fetching free market data:", error);
      setFreeMarketLoading(false);
      setConverterLoading(false);
    }
  };

  const fetchCountriesData = async () => {
    try {
      setCountriesLoading(true);
      const response = await axiosInstance.get("/countries/");
      const validData = response.data.map((country) => ({
        name: country.name || "Unknown", // ✅ match backend
        flag: country.flag || "https://flagcdn.com/w80/un.png",
        timezone: country.timezone || "Unknown", // ✅ match backend
        utc: country.utc || "Unknown",
        mobileCode: country.mobileCode || "Unknown",
      }));

      setCountriesData(validData);
      setCountriesLoading(false);
    } catch (error) {
      console.error("Error fetching countries data:", error);
      setCountriesLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaryData();
    fetchCryptoData();
    fetchFreeMarketData();
    fetchCountriesData();
    dispatch(fetchPrfDashboard(selectedYears.order));
    dispatch(fetchCotDashboard(selectedYears.declaration));
  }, [dispatch, selectedYears.order, selectedYears.declaration]);

  useEffect(() => {
    if (converterData.length > 0) {
      const hasFrom = converterData.some((item) => item.label === fromCurrency);
      const hasTo = converterData.some((item) => item.label === toCurrency);

      if (!hasFrom) {
        setFromCurrency(converterData[0].label);
      }

      if (!hasTo) {
        const fallbackTo =
          converterData.length > 1
            ? converterData[1].label
            : converterData[0].label;
        setToCurrency(fallbackTo);
      }
    }
  }, [converterData, fromCurrency, toCurrency]);

  useEffect(() => {
    if (converterData.length > 0 && fromCurrency && toCurrency) {
      const fromRate =
        converterData.find((c) => c.label === fromCurrency)?.value || 1;
      const toRate =
        converterData.find((c) => c.label === toCurrency)?.value || 1;
      const numericAmount = Number(amount) || 0;
      const result = (numericAmount * fromRate) / toRate;
      setConvertedAmount(result);
      setConversionRate(fromRate / toRate || null);
      setRateTimestamp(new Date().toISOString());
    }
  }, [amount, fromCurrency, toCurrency, converterData]);

  const {
    passed_checks_count,
    unpassed_checks_count,
    passed_checks_value,
    unpassed_checks_value,
  } = summaryData;
  const checksData = {
    labels: ["چک‌های پاس‌شده", "چک‌های پاس‌نشده"],
    datasets: [
      {
        label:
          dataType === "price"
            ? "آمار چک‌ها (بر اساس ارزش)"
            : "آمار چک‌ها (بر اساس تعداد)",
        data:
          dataType === "price"
            ? [passed_checks_value, unpassed_checks_value]
            : [passed_checks_count, unpassed_checks_count],
        // هم‌رنگ با چارت‌های دیگر (emerald / indigo)
        backgroundColor: ["rgba(16, 185, 129, 0.9)", "rgba(79, 70, 229, 0.9)"],
        hoverBackgroundColor: ["rgba(5, 150, 105, 1)", "rgba(55, 48, 163, 1)"],
        borderColor: ["#FFFFFF", "#FFFFFF"],
        borderWidth: 2,
        borderRadius: 6, // دقیقا مثل BarChart
      },
    ],
  };

  const checksOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 8,
        right: 8,
        bottom: 8,
        left: 8,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 16,
          font: {
            family: "'Vazirmatn', sans-serif",
            size: 11,
          },
          color: "#4B5563",
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: {
          family: "'Vazirmatn', sans-serif",
          size: 13,
        },
        bodyFont: {
          family: "'Vazirmatn', sans-serif",
          size: 12,
        },
        padding: 10,
        cornerRadius: 8,
      },
    },
    animation: {
      duration: 900,
      easing: "easeOutQuad",
    },
  };

  // const representationDoughnutData = {
  //   labels: ["وکالت فعال", "وکالت غیرفعال"],
  //   datasets: [
  //     {
  //       data: [unpast_representations_count, past_representations_count],
  //       backgroundColor: ["#3B82F6", "#F59E0B"],
  //       hoverBackgroundColor: ["#2563EB", "#D97706"],
  //       borderColor: ["#FFFFFF", "#FFFFFF"],
  //       borderWidth: 2,
  //       borderRadius: 4,
  //     },
  //   ],
  // };

  // const representationDoughnutOptions = {
  //   responsive: true,
  //   plugins: {
  //     legend: {
  //       position: "top",
  //       labels: {
  //         font: {
  //           family: "'Vazirmatn', sans-serif",
  //           size: 14,
  //           weight: "bold",
  //         },
  //         color: "#1F2937",
  //         padding: 20,
  //       },
  //     },
  //     title: {
  //       display: true,
  //       text: "وکالت‌نامه‌ها (فعال در مقابل غیرفعال)",
  //       font: {
  //         family: "'Vazirmatn', sans-serif",
  //         size: 18,
  //         weight: "bold",
  //       },
  //       color: "#1F2937",
  //       padding: {
  //         top: 10,
  //         bottom: 20,
  //       },
  //     },
  //     tooltip: {
  //       backgroundColor: "rgba(31, 41, 55, 0.9)",
  //       titleFont: {
  //         family: "'Vazirmatn', sans-serif",
  //         size: 14,
  //       },
  //       bodyFont: {
  //         family: "'Vazirmatn', sans-serif",
  //         size: 12,
  //       },
  //       padding: 12,
  //       cornerRadius: 8,
  //     },
  //   },
  //   animation: {
  //     duration: 1000,
  //     easing: "easeOutQuart",
  //   },
  //   cutout: "50%",
  // };

  const { prf_summary, cot_summary, loading, error } = useSelector(
    (state) => state.dashboard,
  );

  const currencyIcons = {
    "دلار آمریکا": "https://flagcdn.com/w80/us.png",
    یورو: "https://flagcdn.com/w80/eu.png",
    "پوند انگلیس": "https://flagcdn.com/w80/gb.png",
    "یکصد ین ژاپن": "https://flagcdn.com/w80/jp.png",
    "فرانک سوئیس": "https://flagcdn.com/w80/ch.png",
    "دلار کانادا": "https://flagcdn.com/w80/ca.png",
    "دلار استرالیا": "https://flagcdn.com/w80/au.png",
    "کرون نروژ": "https://flagcdn.com/w80/no.png",
    "کرون سوئد": "https://flagcdn.com/w80/se.png",
    "کرون دانمارک": "https://flagcdn.com/w80/dk.png",
    "درهم امارات متحده عربی": "https://flagcdn.com/w80/ae.png",
    "ریال عربستان سعودی": "https://flagcdn.com/w80/sa.png",
    "دینار کویت": "https://flagcdn.com/w80/kw.png",
    "دینار بحرین": "https://flagcdn.com/w80/bh.png",
    "ریال قطر": "https://flagcdn.com/w80/qa.png",
    "ریال عمان": "https://flagcdn.com/w80/om.png",
    "روپیه هندوستان": "https://flagcdn.com/w80/in.png",
    "لیر ترکیه": "https://flagcdn.com/w80/tr.png",
    "راند آفریقای جنوبی": "https://flagcdn.com/w80/za.png",
    "یکصد دینار عراق": "https://flagcdn.com/w80/iq.png",
    "یوان چین": "https://flagcdn.com/w80/cn.png",
    "رینگیت مالزی": "https://flagcdn.com/w80/my.png",
    "دلار سنگاپور": "https://flagcdn.com/w80/sg.png",
    "دلار هنگ کنگ": "https://flagcdn.com/w80/hk.png",
    "هزار روپیه اندونزی": "https://flagcdn.com/w80/id.png",
    "هزار وون کره جنوبی": "https://flagcdn.com/w80/kr.png",
    "درام ارمنستان": "https://flagcdn.com/w80/am.png",
    "منات آذربایجان": "https://flagcdn.com/w80/az.png",
    "روبل روسیه": "https://flagcdn.com/w80/ru.png",
    "دلار تایوان": "https://flagcdn.com/w80/tw.png",
    "ریونیا اوکراین": "https://flagcdn.com/w80/ua.png",
    "یک هزار روبل بلاروس": "https://flagcdn.com/w80/by.png",
    "یکصد بات تایلند": "https://flagcdn.com/w80/th.png",
    "نرخ ارز صادراتی":
      "https://cryptologos.cc/logos/generic-cryptocurrency-logo.png",
  };

  const freeMarketIcons = {
    دلار: "https://flagcdn.com/w80/us.png",
    یورو: "https://flagcdn.com/w80/eu.png",
    "درهم امارات": "https://flagcdn.com/w80/ae.png",
    "پوند انگلیس": "https://flagcdn.com/w80/gb.png",
    "لیر ترکیه": "https://flagcdn.com/w80/tr.png",
    "فرانک سوئیس": "https://flagcdn.com/w80/ch.png",
    "یوان چین": "https://flagcdn.com/w80/cn.png",
    "ین ژاپن (100 ین)": "https://flagcdn.com/w80/jp.png",
    "وون کره جنوبی": "https://flagcdn.com/w80/kr.png",
    "دلار کانادا": "https://flagcdn.com/w80/ca.png",
    "دلار استرالیا": "https://flagcdn.com/w80/au.png",
    "دلار نیوزیلند": "https://flagcdn.com/w80/nz.png",
    "دلار سنگاپور": "https://flagcdn.com/w80/sg.png",
    "روپیه هند": "https://flagcdn.com/w80/in.png",
    "روپیه پاکستان": "https://flagcdn.com/w80/pk.png",
    "دینار عراق": "https://flagcdn.com/w80/iq.png",
    "پوند سوریه": "https://flagcdn.com/w80/sy.png",
    افغانی: "https://flagcdn.com/w80/af.png",
    "کرون دانمارک": "https://flagcdn.com/w80/dk.png",
    "کرون سوئد": "https://flagcdn.com/w80/se.png",
    "کرون نروژ": "https://flagcdn.com/w80/no.png",
    "ریال عربستان": "https://flagcdn.com/w80/sa.png",
    "ریال قطر": "https://flagcdn.com/w80/qa.png",
    "ریال عمان": "https://flagcdn.com/w80/om.png",
    "دینار کویت": "https://flagcdn.com/w80/kw.png",
    "دینار بحرین": "https://flagcdn.com/w80/bh.png",
    "رینگیت مالزی": "https://flagcdn.com/w80/my.png",
    "بات تایلند": "https://flagcdn.com/w80/th.png",
    "دلار هنگ کنگ": "https://flagcdn.com/w80/hk.png",
    "روبل روسیه": "https://flagcdn.com/w80/ru.png",
    "منات آذربایجان": "https://flagcdn.com/w80/az.png",
    "درام ارمنستان": "https://flagcdn.com/w80/am.png",
    "لاری گرجستان": "https://flagcdn.com/w80/ge.png",
    "سوم قرقیزستان": "https://flagcdn.com/w80/kg.png",
    "سامانی تاجیکستان": "https://flagcdn.com/w80/tj.png",
    "منات ترکمنستان": "https://flagcdn.com/w80/tm.png",
  };
  const slugToLabel = {
    USD: "دلار آمریکا",
    EUR: "یورو",
    GBP: "پوند انگلیس",
    JPY: "یکصد ین ژاپن",
    CHF: "فرانک سوئیس",
    CAD: "دلار کانادا",
    AUD: "دلار استرالیا",
    NOK: "کرون نروژ",
    SEK: "کرون سوئد",
    DKK: "کرون دانمارک",
    AED: "درهم امارات متحده عربی",
    SAR: "ریال عربستان سعودی",
    KWD: "دینار کویت",
    BHD: "دینار بحرین",
    QAR: "ریال قطر",
    OMR: "ریال عمان",
    INR: "روپیه هندوستان",
    TRY: "لیر ترکیه",
    ZAR: "راند آفریقای جنوبی",
    IQD: "یکصد دینار عراق",
    CNY: "یوان چین",
    MYR: "رینگیت مالزی",
    SGD: "دلار سنگاپور",
    HKD: "دلار هنگ کنگ",
    IDR: "هزار روپیه اندونزی",
    KRW: "هزار وون کره جنوبی",
    AMD: "درام ارمنستان",
    AZN: "منات آذربایجان",
    RUB: "روبل روسیه",
    TWD: "دلار تایوان",
    UAH: "ریونیا اوکراین",
    BYN: "یک هزار روبل بلاروس",
    THB: "یکصد بات تایلند",
  };
  const totalPages = Math.ceil(cryptoData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCurrencies = cryptoData.slice(startIndex, endIndex);

  const totalFreeMarketPages = Math.ceil(freeMarketData.length / itemsPerPage);
  const startFreeMarketIndex = (currentFreeMarketPage - 1) * itemsPerPage;
  const endFreeMarketIndex = startFreeMarketIndex + itemsPerPage;
  const currentFreeMarketCurrencies = freeMarketData.slice(
    startFreeMarketIndex,
    endFreeMarketIndex,
  );

  const getFlagForLabel = (label) => {
    if (!label) return "";
    return (
      freeMarketIcons[label] ||
      currencyIcons[label] ||
      "https://flagcdn.com/w20/un.png"
    );
  };

  const totalCountriesPages = Math.ceil(countriesData.length / itemsPerPage);
  const startCountriesIndex = (currentCountriesPage - 1) * itemsPerPage;
  const endCountriesIndex = startCountriesIndex + itemsPerPage;
  const currentCountries = countriesData.slice(
    startCountriesIndex,
    endCountriesIndex,
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleFreeMarketPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalFreeMarketPages) {
      setCurrentFreeMarketPage(newPage);
    }
  };

  const handleCountriesPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalCountriesPages) {
      setCurrentCountriesPage(newPage);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden mt-20 bg-gray-50 p-4 sm:p-6 md:p-8 lg:p-10 font-vazir">
      <header className="mb-6 text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
          داشبورد مدیریت
        </h1>
        <p className="text-sm sm:text-base text-gray-500 mt-2">
          مروری سریع بر سامانه
        </p>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* ردیف کارت‌های ثبت سفارش، اظهارنامه و چک‌ها */}
        {/* ردیف کارت‌های ثبت سفارش، اظهارنامه و چک‌ها */}
        <div className="w-full flex flex-col md:flex-row gap-4 mb-4 col-span-1 sm:col-span-2 lg:col-span-4">
          {/* Order Card - 40% on md+, full width on mobile */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md flex flex-col w-full md:basis-2/5 min-h-[320px] md:min-h-[380px]">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
              ثبت سفارش
            </h2>
            {loading ? (
              <SkeletonLineChart />
            ) : error ? (
              <p className="text-red-500 text-sm">خطا: {error}</p>
            ) : (
              <div className="flex-1">
                <LineChartComponent
                  prf_summary={prf_summary}
                  selectedYear={selectedYears.order}
                  onYearChange={(year) => handleYearChange("order", year)}
                />
              </div>
            )}
          </div>

          {/* Declaration Card - 40% on md+, full width on mobile */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md flex flex-col w-full md:basis-2/5 min-h-[320px] md:min-h-[380px]">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
              اظهارنامه
            </h2>
            {loading ? (
              <SkeletonBarChart />
            ) : error ? (
              <p className="text-red-500 text-sm">خطا: {error}</p>
            ) : (
              <div className="flex-1">
                <BarChartComponent
                  cot_summary={cot_summary}
                  selectedYear={selectedYears.declaration}
                  onYearChange={(year) => handleYearChange("declaration", year)}
                />
              </div>
            )}
          </div>

          {/* Checks Card - 20% on md+, full width on mobile */}
          {/* Checks Card - 20% on md+, full width on mobile */}
          {/* Checks Card - 20% on md+, full width on mobile */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md flex flex-col w-full md:basis-1/5">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
              چک‌ها
            </h2>

            {/* Controls – دقیقا همان استایل BarChart */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex items-center justify-between gap-2 text-xs sm:text-sm">
                <span className="text-slate-600">
                  تعداد کل چک‌ها:{" "}
                  {(passed_checks_count + unpassed_checks_count).toLocaleString(
                    "fa-IR",
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-slate-600">نمایش:</span>
                <div className="inline-flex rounded-full bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setDataType("price")}
                    className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      dataType === "price"
                        ? "bg-emerald-500 text-white shadow-sm"
                        : "text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    ارزش
                  </button>
                  <button
                    type="button"
                    onClick={() => setDataType("count")}
                    className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      dataType === "count"
                        ? "bg-indigo-500 text-white shadow-sm"
                        : "text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    تعداد
                  </button>
                </div>
              </div>
            </div>

            {/* Pie chart area */}
            <div className="flex-1 min-h-[200px] sm:min-h-[220px] md:min-h-[240px]">
              {loading ? (
                <SkeletonPieChart />
              ) : (
                <PieChartComponent data={checksData} options={checksOptions} />
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <SkeletonBarChart />
          </div>
        ) : error ? (
          <div className="col-span-1 sm:col-span-2 lg:col-span-4 text-sm text-red-500">
            خطا در دریافت مانده ثبت سفارش‌ها
          </div>
        ) : (
          <RemainingOrderBalancesChart
            balanceData={prf_summary?.balance_data || []}
          />
        )}

        {/* Representation Card */}
        {/* <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md flex flex-col">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
            وکالت
          </h2>
          <ul className="mb-4 text-sm text-gray-600">
            <li>
              تعداد کل وکالت‌نامه‌ها:{" "}
              {past_representations_count + unpast_representations_count}
            </li>
          </ul>
          {loading ? (
            <SkeletonDoughnutChart />
          ) : (
            <DoughnutChartComponent
              data={representationDoughnutData}
              options={representationDoughnutOptions}
            />
          )}
          <Link to="/representations" className="mt-4">
            <button className="w-full px-4 py-2 bg-indigo-500 text-white rounded-md text-sm font-medium hover:bg-indigo-600 transition-colors">
              مشاهده وکالت‌نامه‌ها
            </button>
          </Link>
        </div> */}
        {/* Currency Exchange Rates Card (نرخ ارز نیمایی) */}
        {/* نرخ ارز مرکز مبادله */}
        <div className="bg-white/80 backdrop-blur-sm border border-slate-100 p-4 sm:p-6 rounded-2xl shadow-sm flex flex-col col-span-1 sm:col-span-2 lg:col-span-4">
          <div className="flex flex-row-reverse items-center justify-between mb-4">
            <div>
              <h2 className="text-lg text-right sm:text-xl font-semibold text-slate-900">
                نرخ ارز مرکز مبادله
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                آخرین نرخ‌های اعلام‌شده توسط مرکز مبادله
              </p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[0.7rem] sm:text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
              وضعیت: زنده
            </span>
          </div>

          {cryptoLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse bg-slate-100/80 rounded-xl h-24 border border-slate-100"
                ></div>
              ))}
            </div>
          ) : cryptoData.length === 0 ? (
            <p className="text-slate-500 text-sm">
              داده‌ای برای نمایش وجود ندارد
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {currentCurrencies.map((currency, index) => {
                  const iconLabel =
                    slugToLabel[currency.slug] || currency.label;
                  const iconUrl =
                    currencyIcons[iconLabel] ||
                    "https://cryptologos.cc/logos/generic-cryptocurrency-logo.png";

                  return (
                    <div
                      key={index}
                      className="flex items-center p-4 rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-sky-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                    >
                      <img
                        src={iconUrl}
                        alt={currency.label}
                        className="w-10 h-10 mr-3 rounded-full object-cover border border-slate-200"
                      />
                      <div className="flex-1">
                        <h3 className="text-sm sm:text-base font-semibold text-slate-900">
                          {currency.label}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-600">
                          فروش: {currency.value.toLocaleString("fa-IR")} ﷼
                        </p>
                        <p className="text-[0.7rem] sm:text-xs text-slate-500">
                          خرید: {currency.buyPrice.toLocaleString("fa-IR")} ﷼
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex justify-center items-center">
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2 py-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        currentPage === 1
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      صفحه قبلی
                    </button>
                    <span className="px-2 text-xs sm:text-sm text-slate-600">
                      صفحه {currentPage} از {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        currentPage === totalPages
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      صفحه بعدی
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* نرخ ارز بازار آزاد */}
        <div className="bg-white/80 backdrop-blur-sm border border-slate-100 p-4 sm:p-6 rounded-2xl shadow-sm flex flex-col col-span-1 sm:col-span-2 lg:col-span-4">
          <div className="flex flex-row-reverse items-center justify-between mb-4">
            <div>
              <h2 className="text-lg text-right sm:text-xl font-semibold text-slate-900">
                نرخ ارز بازار آزاد
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                نمایش نرخ لحظه‌ای بازار آزاد
              </p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[0.7rem] sm:text-xs font-medium bg-sky-50 text-sky-700 border border-sky-100">
              بازار آزاد
            </span>
          </div>

          {freeMarketLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse bg-slate-100/80 rounded-xl h-24 border border-slate-100"
                ></div>
              ))}
            </div>
          ) : freeMarketData.length === 0 ? (
            <p className="text-slate-500 text-sm">
              داده‌ای برای نمایش وجود ندارد
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {currentFreeMarketCurrencies.map((currency, index) => (
                  <div
                    key={index}
                    className="flex items-center p-4 rounded-xl border border-slate-100 bg-gradient-to-r from-teal-50 to-blue-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    <img
                      src={
                        freeMarketIcons[currency.label] ||
                        "https://cryptologos.cc/logos/generic-cryptocurrency-logo.png"
                      }
                      alt={currency.label}
                      className="w-10 h-10 mr-3 rounded-full object-cover border border-slate-200"
                    />
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-slate-900">
                        {currency.label}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        ارزش:{" "}
                        {currency.value
                          ? currency.value.toLocaleString("fa-IR") + " ﷼"
                          : "نامشخص"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {totalFreeMarketPages > 1 && (
                <div className="mt-4 flex justify-center items-center">
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2 py-1">
                    <button
                      onClick={() =>
                        handleFreeMarketPageChange(currentFreeMarketPage - 1)
                      }
                      disabled={currentFreeMarketPage === 1}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        currentFreeMarketPage === 1
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      صفحه قبلی
                    </button>
                    <span className="px-2 text-xs sm:text-sm text-slate-600">
                      صفحه {currentFreeMarketPage} از {totalFreeMarketPages}
                    </span>
                    <button
                      onClick={() =>
                        handleFreeMarketPageChange(currentFreeMarketPage + 1)
                      }
                      disabled={currentFreeMarketPage === totalFreeMarketPages}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        currentFreeMarketPage === totalFreeMarketPages
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      صفحه بعدی
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* اطلاعات کشورها */}
        <div className="bg-white/80 backdrop-blur-sm border border-slate-100 p-4 sm:p-6 rounded-2xl shadow-sm flex flex-col col-span-1 sm:col-span-2 lg:col-span-4">
          <div className="flex flex-row-reverse items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
              اطلاعات کشورها
            </h2>
            <span className="text-[0.7rem] sm:text-xs text-slate-500">
              تعداد کشورها: {countriesData.length}
            </span>
          </div>

          {countriesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse bg-slate-100/80 rounded-xl h-24 border border-slate-100"
                ></div>
              ))}
            </div>
          ) : countriesData.length === 0 ? (
            <p className="text-slate-500 text-sm text-left">
              No data available to display
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {currentCountries.map((country, index) => (
                  <div
                    key={index}
                    className="flex items-center p-4 rounded-xl border border-slate-100 bg-gradient-to-r from-blue-50 to-emerald-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                    dir="ltr"
                  >
                    <img
                      src={country.flag}
                      alt={country.name}
                      className="w-10 h-10 mr-3 rounded-full object-cover border border-slate-200"
                    />
                    <div className="text-left">
                      <h3 className="text-sm sm:text-base font-semibold text-slate-900">
                        {country.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 flex items-center">
                        <FaGlobe className="mr-1" /> Timezone:{" "}
                        {country.timezone}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-600 flex items-center">
                        <FaClock className="mr-1" /> UTC: {country.utc}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-600 flex items-center">
                        <FaPhone className="mr-1" /> Mobile Code:{" "}
                        {country.mobileCode}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {totalCountriesPages > 1 && (
                <div className="mt-4 flex justify-center items-center">
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2 py-1">
                    <button
                      onClick={() =>
                        handleCountriesPageChange(currentCountriesPage - 1)
                      }
                      disabled={currentCountriesPage === 1}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        currentCountriesPage === 1
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      صفحه قبلی
                    </button>
                    <span className="px-2 text-xs sm:text-sm text-slate-600">
                      صفحه {currentCountriesPage} از {totalCountriesPages}
                    </span>
                    <button
                      onClick={() =>
                        handleCountriesPageChange(currentCountriesPage + 1)
                      }
                      disabled={currentCountriesPage === totalCountriesPages}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        currentCountriesPage === totalCountriesPages
                          ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      صفحه بعدی
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Currency Converter Card */}
        <div
          className="bg-white/80 backdrop-blur-sm border border-slate-100 p-4 sm:p-6 rounded-2xl shadow-sm flex flex-col col-span-1 sm:col-span-2 lg:col-span-4"
          dir="ltr"
        >
          <div className="flex flex-row-reverse items-center justify-between mb-4">
            <div>
              <h2 className="text-lg text-right sm:text-xl font-semibold text-slate-900">
                تبدیل ارز
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                تبدیل سریع بین ارزهای مختلف با آخرین نرخ‌ها
              </p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[0.7rem] sm:text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
              Converter
            </span>
          </div>

          {converterLoading ? (
            <div className="animate-pulse bg-slate-100/80 h-28 rounded-2xl border border-slate-100"></div>
          ) : converterData.length === 0 ? (
            <p className="text-slate-500 text-sm text-center">
              داده‌ای برای تبدیل وجود ندارد
            </p>
          ) : (
            <div className="space-y-4">
              <div className="w-full rounded-2xl border border-slate-100 shadow-sm overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50">
                <div className="flex flex-col lg:flex-row items-stretch relative">
                  {/* FROM */}
                  <div className="flex-1 px-4 py-4 border-b lg:border-b-0 lg:border-r border-slate-100 bg-white/80">
                    <p className="text-[0.7rem] sm:text-xs text-slate-500 mb-2">
                      From
                    </p>
                    <div className="flex flex-col sm:flex-row items-stretch gap-3">
                      <input
                        type="number"
                        step="any"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        min="0"
                        className="w-full sm:flex-1 rounded-xl border border-slate-200 px-3 py-3 text-xl sm:text-2xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-white shadow-xs"
                      />
                      {/* DROPDOWN WRAPPER – width controlled on desktop */}
                      <div className="relative w-full sm:w-48 lg:w-56">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
                          <img
                            src={getFlagForLabel(fromCurrency)}
                            alt={fromCurrency}
                            className="w-5 h-5 rounded-full border border-slate-200"
                          />
                        </div>
                        <select
                          value={fromCurrency}
                          onChange={(e) => setFromCurrency(e.target.value)}
                          className="w-full appearance-none rounded-xl border border-slate-200 bg-white pl-10 pr-8 py-3 text-xs sm:text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                        >
                          {converterData.map((currency, index) => (
                            <option key={index} value={currency.label}>
                              {currency.label}
                            </option>
                          ))}
                        </select>
                        <FaChevronDown className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-slate-400 text-xs" />
                      </div>
                    </div>
                  </div>

                  {/* SWITCH BUTTON */}
                  <div className="flex items-center justify-center px-4 py-4 bg-slate-50 lg:border-r border-slate-100">
                    <button
                      type="button"
                      onClick={() => {
                        setFromCurrency(toCurrency);
                        setToCurrency(fromCurrency);
                      }}
                      className="h-11 w-11 flex items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-600 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition-all shadow-xs"
                    >
                      <FaExchangeAlt className="text-lg" />
                    </button>
                  </div>

                  {/* TO */}
                  <div className="flex-1 px-4 py-4 bg-white/80">
                    <p className="text-[0.7rem] sm:text-xs text-slate-500 mb-2">
                      To
                    </p>
                    <div className="flex flex-col sm:flex-row items-stretch gap-3">
                      <input
                        type="text"
                        readOnly
                        value={
                          convertedAmount !== null
                            ? convertedAmount.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 6,
                              })
                            : ""
                        }
                        className="w-full sm:flex-1 rounded-xl border border-slate-200 px-3 py-3 text-xl sm:text-2xl font-semibold text-slate-900 bg-slate-50 focus:outline-none"
                      />
                      {/* DROPDOWN WRAPPER – same widths as FROM */}
                      <div className="relative w-full sm:w-48 lg:w-56">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
                          <img
                            src={getFlagForLabel(toCurrency)}
                            alt={toCurrency}
                            className="w-5 h-5 rounded-full border border-slate-200"
                          />
                        </div>
                        <select
                          value={toCurrency}
                          onChange={(e) => setToCurrency(e.target.value)}
                          className="w-full appearance-none rounded-xl border border-slate-200 bg-white pl-10 pr-8 py-3 text-xs sm:text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                        >
                          {converterData.map((currency, index) => (
                            <option key={index} value={currency.label}>
                              {currency.label}
                            </option>
                          ))}
                        </select>
                        <FaChevronDown className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-slate-400 text-xs" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* FOOTER SUMMARY */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-3 gap-3 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
                  <p className="text-sm sm:text-base font-semibold text-slate-900">
                    {fromCurrency && toCurrency && convertedAmount !== null
                      ? ` ${fromCurrency}${Number(amount || 0).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}  = ${convertedAmount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 6,
                        })} ${toCurrency}`
                      : "در حال محاسبه..."}
                  </p>
                  <span className="text-[0.7rem] sm:text-xs text-slate-500">
                    {conversionRate
                      ? `Mid-market rate at ${
                          rateTimestamp
                            ? new Date(rateTimestamp).toLocaleTimeString(
                                "en-GB",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                            : ""
                        }`
                      : "Mid-market rate در حال بروزرسانی"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
